import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { 
  FileText, 
  Upload, 
  Table, 
  PenLine,
  ArrowRight,
  CheckCircle,
  Sparkles,
  FolderOpen,
  FileSpreadsheet,
  Building2,
  ChevronRight,
  HelpCircle,
  FileCheck,
  X,
  CreditCard,
  Receipt,
  Calculator,
  AlertCircle,
  BookOpen
} from 'lucide-react';

type InputMethod = 'upload' | 'spreadsheet' | 'manual' | null;
type PLStatus = 'yes' | 'no' | null;

// Redirect to wizard - this page is deprecated but kept for backwards compatibility
function NewClaimRedirect() {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect to wizard
    navigate('/wizard-v2', { replace: true });
  }, [navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
      <p className="cs-meta">Redirecting to wizard...</p>
    </div>
  );
}

// Keep redirect available if needed
export { NewClaimRedirect };

// Document Guide Modal Component
function DocumentGuideModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null;

  const alternativeDocs = [
    { 
      icon: FileText, 
      name: 'Tax Returns (Form 1065, K-1)', 
      description: 'State and federal returns from the last 2-3 years verify income history',
      priority: 'high'
    },
    { 
      icon: CreditCard, 
      name: 'Bank Statements', 
      description: 'Provides objective cash flow data to trace revenues and expenses',
      priority: 'high'
    },
    { 
      icon: Receipt, 
      name: 'Sales & Revenue Reports', 
      description: 'Daily/monthly revenue records and customer orders show income patterns',
      priority: 'high'
    },
    { 
      icon: FileCheck, 
      name: 'Payroll Records', 
      description: 'Employee wage logs support continuing expense claims',
      priority: 'medium'
    },
    { 
      icon: FileText, 
      name: 'Invoices & Contracts', 
      description: 'Supplier contracts and customer orders prove lost opportunities',
      priority: 'medium'
    },
    { 
      icon: Calculator, 
      name: 'Budgets & Forecasts', 
      description: 'Pre-loss projections support "but-for" revenue estimates',
      priority: 'medium'
    },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />

        {/* Modal */}
        <div className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ background: 'var(--color-bg-surface)', borderRadius: 14, border: '1px solid var(--color-border)', boxShadow: '0 20px 50px rgba(0,0,0,0.35)' }}>
          {/* Header */}
          <div className="sticky top-0 px-6 py-4 flex items-center justify-between" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border-sub)', borderTopLeftRadius: 14, borderTopRightRadius: 14 }}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg" style={{ background: 'var(--color-needs-human-tint)' }}>
                <BookOpen className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              </div>
              <div>
                <h2 className="cs-h2">Filing Without P&L Statements</h2>
                <p className="cs-meta">Alternative documentation guide</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg" style={{ color: 'var(--color-text-muted)' }}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Intro */}
            <div className="rounded-xl p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
                <strong>Good news:</strong> If monthly P&L statements are unavailable, you can still file a
                business interruption claim using alternative financial documents. Our AI-powered system
                can <strong>reconstruct your P&L</strong> from bank statements and other records.
              </p>
            </div>

            {/* Alternative Documents */}
            <div>
              <h3 className="cs-h3" style={{ marginBottom: 12 }}>Accepted Alternative Documents</h3>
              <div className="space-y-3">
                {alternativeDocs.map((doc, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{
                    background: doc.priority === 'high' ? 'var(--color-needs-human-tint)' : 'var(--color-bg-elevated)',
                    border: doc.priority === 'high' ? '1px solid var(--color-needs-human-tint-bd)' : '1px solid var(--color-border)'
                  }}>
                    <div className="p-2 rounded-lg" style={{
                      background: doc.priority === 'high' ? 'var(--color-needs-human-tint)' : 'var(--color-bg-surface)'
                    }}>
                      <doc.icon className="w-4 h-4" style={{
                        color: doc.priority === 'high' ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)'
                      }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{doc.name}</p>
                        {doc.priority === 'high' && (
                          <span className="cs-chip px-2 py-0.5 rounded-full" style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', fontWeight: 500 }}>
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="cs-meta">{doc.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* How It Works */}
            <div>
              <h3 className="cs-h3" style={{ marginBottom: 12 }}>How P&L Reconstruction Works</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2" style={{ background: 'var(--color-needs-human-tint)' }}>
                    <span className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>1</span>
                  </div>
                  <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>Upload Documents</p>
                  <p className="cs-meta" style={{ marginTop: 4 }}>Bank statements, tax returns, receipts</p>
                </div>
                <div className="text-center p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2" style={{ background: 'var(--color-needs-human-tint)' }}>
                    <span className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>2</span>
                  </div>
                  <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>AI Classification</p>
                  <p className="cs-meta" style={{ marginTop: 4 }}>Transactions auto-categorized to expense types</p>
                </div>
                <div className="text-center p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2" style={{ background: 'var(--color-needs-human-tint)' }}>
                    <span className="cs-figure" style={{ color: 'var(--color-needs-human-text)', fontWeight: 700 }}>3</span>
                  </div>
                  <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>P&L Generated</p>
                  <p className="cs-meta" style={{ marginTop: 4 }}>Reconstructed statement validated against benchmarks</p>
                </div>
              </div>
            </div>

            {/* Industry Benchmarks Note */}
            <div className="rounded-xl p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
                <div>
                  <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>Industry Benchmark Validation</p>
                  <p className="cs-body" style={{ marginTop: 4, color: 'var(--color-text-secondary)' }}>
                    Your reconstructed P&L is automatically compared against industry averages (using BLS data)
                    to validate COGS ratios, expense categories, and profit margins. This adds credibility to
                    claims filed without traditional P&L statements.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 px-6 py-4 flex justify-end gap-3" style={{ background: 'var(--color-bg-elevated)', borderTop: '1px solid var(--color-border-sub)', borderBottomLeftRadius: 14, borderBottomRightRadius: 14 }}>
            <button
              onClick={onClose}
              className="cs-body px-4 py-2"
              style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}
            >
              Close
            </button>
            <button
              onClick={onClose}
              className="cs-body px-4 py-2 rounded-lg"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}
            >
              Got it, let's continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// NewClaimLegacy - the full P&L question flow
function NewClaimLegacy() {
  const navigate = useNavigate();
  const [selectedMethod, setSelectedMethod] = useState<InputMethod>(null);
  const [hoveredMethod, setHoveredMethod] = useState<InputMethod>(null);
  const [plStatus, setPLStatus] = useState<PLStatus>(null);
  const [showDocGuide, setShowDocGuide] = useState(false);

  const methods = [
    {
      id: 'upload' as InputMethod,
      icon: Upload,
      title: 'Upload Documents',
      subtitle: 'Bank statements, tax docs, PDFs',
      description: 'Best for raw financial documents that need AI-powered cleanup and categorization.',
      descriptionNoPL: 'Perfect for P&L reconstruction from bank statements and tax documents.',
      features: [
        { icon: Sparkles, text: 'AI Transaction Categorization' },
        { icon: CheckCircle, text: 'Messiness Detection' },
        { icon: FolderOpen, text: 'P&L Reconstruction' },
      ],
      color: 'blue',
      recommended: true,
      recommendedNoPL: true,
      path: '/bulk-upload?mode=documents'
    },
    {
      id: 'spreadsheet' as InputMethod,
      icon: FileSpreadsheet,
      title: 'Import Spreadsheet',
      subtitle: 'CSV, Excel, QuickBooks exports',
      description: 'Best for pre-organized financial data that\'s already categorized.',
      descriptionNoPL: 'Use if you have transaction data exported from your accounting software.',
      features: [
        { icon: Table, text: 'Auto-detect columns' },
        { icon: CheckCircle, text: 'Data validation' },
        { icon: ArrowRight, text: 'Quick import' },
      ],
      color: 'emerald',
      recommended: false,
      recommendedNoPL: false,
      path: '/bulk-upload?mode=spreadsheet'
    },
    {
      id: 'manual' as InputMethod,
      icon: PenLine,
      title: 'Manual Entry',
      subtitle: 'Enter data directly in wizard',
      description: 'Best when you have summary financials ready or want full control.',
      descriptionNoPL: 'Enter reconstructed figures manually if you\'ve already calculated them.',
      features: [
        { icon: Building2, text: 'Industry templates' },
        { icon: CheckCircle, text: 'Guided process' },
        { icon: ArrowRight, text: 'Start immediately' },
      ],
      color: 'blue',
      recommended: false,
      recommendedNoPL: false,
      path: '/wizard-v2'
    }
  ];

  const getColorStyles = (color: string, isSelected: boolean, isHovered: boolean) => {
    // Design system: single orange accent for selection/attention, grayscale otherwise.
    const cardStyle: React.CSSProperties = {
      background: isSelected
        ? 'var(--color-needs-human-tint)'
        : isHovered
        ? 'var(--color-bg-elevated)'
        : 'var(--color-bg-surface)',
      border: isSelected ? '1px solid var(--color-brand)' : '1px solid var(--color-border)',
      boxShadow: isSelected ? '0 0 0 3px var(--color-needs-human-tint)' : 'none',
    };
    const iconStyle: React.CSSProperties = {
      background: 'var(--color-needs-human-tint)',
      color: 'var(--color-needs-human-text)',
    };
    const textStyle: React.CSSProperties = { color: 'var(--color-needs-human-text)' };
    return { cardStyle, iconStyle, textStyle };
  };

  const handleContinue = () => {
    const method = methods.find(m => m.id === selectedMethod);
    if (method) {
      // Pass P&L status to next step
      const path = plStatus === 'no' 
        ? `${method.path}${method.path.includes('?') ? '&' : '?'}noPL=true`
        : method.path;
      navigate(path);
    }
  };

  const isRecommended = (method: typeof methods[0]) => {
    if (plStatus === 'no') return method.recommendedNoPL;
    return method.recommended;
  };

  return (
    <AppLayout>
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" style={{ background: 'var(--color-bg-base)', minHeight: '100%' }}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="cs-display">Create New Claim</h1>
          <p className="cs-meta" style={{ marginTop: 4 }}>Let's gather your financial information</p>
        </div>

        {/* Step 1: P&L Status Question */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="cs-figure w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}>1</div>
            <h2 className="cs-h2">Do you have monthly Profit & Loss (P&L) statements?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Yes Option */}
            <button
              onClick={() => { setPLStatus('yes'); setSelectedMethod(null); }}
              className="p-4 rounded-xl text-left transition-all"
              style={{
                border: plStatus === 'yes' ? '2px solid var(--color-brand)' : '2px solid var(--color-border)',
                background: plStatus === 'yes' ? 'var(--color-needs-human-tint)' : 'var(--color-bg-surface)',
                boxShadow: plStatus === 'yes' ? '0 0 0 3px var(--color-needs-human-tint)' : 'none',
              }}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: plStatus === 'yes' ? 'var(--color-needs-human-tint)' : 'var(--color-bg-elevated)' }}>
                  <CheckCircle className="w-5 h-5" style={{ color: plStatus === 'yes' ? 'var(--color-needs-human-text)' : 'var(--color-text-dim)' }} />
                </div>
                <div>
                  <p className="cs-body" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>Yes, I have P&L statements</p>
                  <p className="cs-meta">Monthly or quarterly financial statements available</p>
                </div>
              </div>
            </button>

            {/* No Option */}
            <button
              onClick={() => { setPLStatus('no'); setSelectedMethod('upload'); }}
              className="p-4 rounded-xl text-left transition-all"
              style={{
                border: plStatus === 'no' ? '2px solid var(--color-brand)' : '2px solid var(--color-border)',
                background: plStatus === 'no' ? 'var(--color-needs-human-tint)' : 'var(--color-bg-surface)',
                boxShadow: plStatus === 'no' ? '0 0 0 3px var(--color-needs-human-tint)' : 'none',
              }}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: plStatus === 'no' ? 'var(--color-needs-human-tint)' : 'var(--color-bg-elevated)' }}>
                  <FileText className="w-5 h-5" style={{ color: plStatus === 'no' ? 'var(--color-needs-human-text)' : 'var(--color-text-dim)' }} />
                </div>
                <div>
                  <p className="cs-body" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>No, I need to use alternative documents</p>
                  <p className="cs-meta">Bank statements, tax returns, or other records</p>
                </div>
              </div>
            </button>
          </div>

          {/* No P&L Info Banner */}
          {plStatus === 'no' && (
            <div className="mt-4 rounded-xl p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
                <div className="flex-1">
                  <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>No problem! We can reconstruct your P&L</p>
                  <p className="cs-body" style={{ marginTop: 4, color: 'var(--color-text-secondary)' }}>
                    Our AI-powered system can reconstruct a P&L statement from bank statements, tax returns (Form 1065, K-1),
                    and other financial records. The reconstructed data is validated against industry benchmarks.
                  </p>
                  <button
                    onClick={() => setShowDocGuide(true)}
                    className="cs-meta mt-2 font-medium flex items-center gap-1"
                    style={{ color: 'var(--color-needs-human-text)' }}
                  >
                    <HelpCircle className="w-4 h-4" />
                    Learn more about alternative documentation
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Method Selection (only show after P&L status is selected) */}
        {plStatus && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <div className="cs-figure w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}>2</div>
              <h2 className="cs-h2">Choose your input method</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {methods.map((method) => {
                const isSelected = selectedMethod === method.id;
                const isHovered = hoveredMethod === method.id;
                const colors = getColorStyles(method.color, isSelected, isHovered);
                const recommended = isRecommended(method);

                return (
                  <button
                    key={method.id}
                    onClick={() => setSelectedMethod(method.id)}
                    onMouseEnter={() => setHoveredMethod(method.id)}
                    onMouseLeave={() => setHoveredMethod(null)}
                    className="relative p-5 rounded-xl text-left transition-all duration-200"
                    style={colors.cardStyle}
                  >
                    {/* Recommended Badge */}
                    {recommended && (
                      <div className="cs-chip absolute -top-2.5 left-4 px-2 py-0.5 rounded-full" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}>
                        {plStatus === 'no' ? 'Best for P&L Reconstruction' : 'Recommended'}
                      </div>
                    )}

                    {/* Selection Indicator */}
                    {isSelected && (
                      <div className="absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center" style={colors.iconStyle}>
                        <CheckCircle className="w-4 h-4" />
                      </div>
                    )}

                    {/* Icon */}
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={colors.iconStyle}>
                      <method.icon className="w-6 h-6" />
                    </div>

                    {/* Title & Subtitle */}
                    <h3 className="cs-h3" style={{ marginBottom: 4 }}>{method.title}</h3>
                    <p className="cs-meta" style={{ marginBottom: 12 }}>{method.subtitle}</p>

                    {/* Description */}
                    <p className="cs-body" style={{ marginBottom: 16, color: 'var(--color-text-secondary)' }}>
                      {plStatus === 'no' ? method.descriptionNoPL : method.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-2">
                      {method.features.map((feature, i) => (
                        <div key={i} className="cs-body flex items-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                          <feature.icon className="w-4 h-4" style={colors.textStyle} />
                          <span>{feature.text}</span>
                        </div>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Continue Button */}
        <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
          <button
            onClick={() => navigate('/')}
            className="cs-body px-4 py-2"
            style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={!selectedMethod || !plStatus}
            className="cs-body flex items-center gap-2 px-6 py-2.5 rounded-lg transition-all"
            style={
              selectedMethod && plStatus
                ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500, boxShadow: '0 8px 20px var(--color-needs-human-tint)' }
                : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-ghost)', fontWeight: 500, cursor: 'not-allowed' }
            }
          >
            Continue
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
          <h4 className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: 8 }}>Need help?</h4>
          <p className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>
            {plStatus === 'no' ? (
              <>
                <strong>Upload Documents</strong> is the best choice for reconstructing P&L from alternative records.
                Our AI will automatically categorize transactions and compare against industry benchmarks.{' '}
                <button onClick={() => setShowDocGuide(true)} style={{ color: 'var(--color-needs-human-text)', textDecoration: 'underline' }}>
                  View documentation guide →
                </button>
              </>
            ) : (
              <>
                <strong>Upload Documents</strong> is best for most users. Our AI will automatically
                categorize your transactions, detect data quality issues, and reconstruct your P&L
                statement from raw bank statements or tax documents.
              </>
            )}
          </p>
        </div>

        {/* Document Guide Link */}
        <div className="mt-4 text-center">
          <Link
            to="/docs-guide"
            className="cs-meta flex items-center justify-center gap-1"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <BookOpen className="w-4 h-4" />
            View complete documentation guide
          </Link>
        </div>
      </div>

      {/* Document Guide Modal */}
      <DocumentGuideModal isOpen={showDocGuide} onClose={() => setShowDocGuide(false)} />
    </AppLayout>
  );
}

// Export the full NewClaim component with P&L question as default
export default NewClaimLegacy;
